import { useState } from 'react'
import { useSocket } from '../../hooks/useSocket'
import { normalizeMessage } from '../../types'

interface ChatProps {
  isLive: boolean
}

export function Chat({ isLive }: ChatProps) {
  const [nickname, setNickname] = useState('')
  const [nicknameSet, setNicknameSet] = useState(false)
  const [messageInput, setMessageInput] = useState('')

  const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000'
  const { isConnected, messages, sendMessage } = useSocket({
    url: API_URL,
    autoConnect: true,
  })

  const handleSetNickname = (e: React.FormEvent) => {
    e.preventDefault()
    if (nickname.trim()) {
      setNicknameSet(true)
    }
  }

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault()
    if (messageInput.trim() && nickname.trim()) {
      sendMessage(nickname, messageInput)
      setMessageInput('')
    }
  }

  return (
    <div className="h-full flex flex-col">
      {/* Connection Status */}
      <div className="px-4 py-2 border-b border-[var(--border-color)] bg-[var(--bg-tertiary)]">
        <div className="flex items-center justify-between text-xs">
          <div className="flex items-center gap-2">
            <span className={`w-2 h-2 rounded-full ${isConnected ? 'bg-[var(--success)]' : 'bg-gray-500'}`}></span>
            <span className="text-[var(--text-secondary)]">
              {nicknameSet ? (
                <>מחובר בשם: <span className="text-[var(--text-primary)] font-medium">{nickname}</span></>
              ) : (
                isConnected ? 'מחובר' : 'מנותק'
              )}
            </span>
          </div>
          {nicknameSet && (
            <button
              onClick={() => setNicknameSet(false)}
              className="text-[var(--accent)] hover:underline"
            >
              שנה כינוי
            </button>
          )}
        </div>
      </div>

      {/* Messages List */}
      <div className="flex-1 overflow-y-auto p-4 flex flex-col-reverse gap-3">
        {!isLive ? (
          <div className="text-center text-[var(--text-secondary)] text-sm py-8">
            הצ'אט יפעל בזמן שידור
          </div>
        ) : messages.length === 0 ? (
          <div className="text-center text-[var(--text-secondary)] text-sm py-8">
            אין הודעות עדיין
          </div>
        ) : (
          messages.slice().reverse().map((msg) => {
            const normalized = normalizeMessage(msg)
            return (
              <div
                key={normalized.id}
                className={`p-3 rounded-lg ${
                  normalized.is_broadcaster
                    ? 'bg-[var(--accent)]/10 border border-[var(--accent)]/30'
                    : 'bg-[var(--chat-user)]'
                }`}
              >
                <div className="flex items-baseline gap-2 mb-1">
                  <span className={`font-semibold text-sm ${
                    normalized.is_broadcaster ? 'text-[var(--accent)]' : 'text-[var(--text-primary)]'
                  }`}>
                    {normalized.is_broadcaster && '🎙️ '}
                    {normalized.sender_name}
                  </span>
                </div>
                <p className="text-[var(--text-primary)] text-sm break-words">
                  {normalized.message}
                </p>
              </div>
            )
          })
        )}
      </div>

      {/* Message Input or Nickname Prompt */}
      <div className="p-4 border-t border-[var(--border-color)] bg-[var(--bg-secondary)]">
        {nicknameSet ? (
          <form onSubmit={handleSendMessage} className="flex gap-2">
            <input
              type="text"
              value={messageInput}
              onChange={(e) => setMessageInput(e.target.value)}
              placeholder={isLive ? "הקלד הודעה..." : "הצ'אט יפעל בזמן שידור"}
              maxLength={500}
              disabled={!isLive || !isConnected}
              className="flex-1 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)] disabled:opacity-50 text-sm"
            />
            <button
              type="submit"
              disabled={!isLive || !messageInput.trim() || !isConnected}
              className="px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Send message"
            >
              📤
            </button>
          </form>
        ) : (
          <form onSubmit={handleSetNickname} className="space-y-3">
            <p className="text-xs text-[var(--text-secondary)] text-center">
              הכנס כינוי כדי לשלוח הודעות
            </p>
            <div className="flex gap-2">
              <input
                type="text"
                value={nickname}
                onChange={(e) => setNickname(e.target.value)}
                placeholder="הכנס כינוי..."
                maxLength={20}
                className="flex-1 px-4 py-2 bg-[var(--bg-tertiary)] border border-[var(--border-color)] rounded-lg text-[var(--text-primary)] placeholder-[var(--text-secondary)] focus:outline-none focus:border-[var(--accent)] text-sm"
              />
              <button
                type="submit"
                disabled={!nickname.trim()}
                className="px-4 py-2 bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm"
              >
                שמור
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
